/* eslint-disable react-hooks/exhaustive-deps */
'use client'
import { SubmissionSteps } from "./reportIndividualPage";
import { useEffect, useState, useRef } from 'react';
import { useFormContext, EventMediaType } from "./formContext";
import { TitleAndInput } from "./inputFields";
import { StepNavigation } from "./stepNavigation";
import { isValidUrl, Direction } from './utils';
import { DropZone } from "./dropZone";
import SvgIcon from "../layout/svg-icons/svg-icons";
import { addIcon, fileIcon, getPhotoSizeString, trash, videoIcon } from "../reportingTool/reportingUtils";

interface IProps {
  setCurrentStep: (Step: SubmissionSteps) => void;
  markStepAsCompleted: (step: SubmissionSteps) => void;
  currentStep: SubmissionSteps;
}

export enum FootageType {
  Online = 'online',
  Personal = 'personal'
}

export const MediaEvidence: React.FC<IProps> = ({ setCurrentStep, markStepAsCompleted, currentStep }: IProps) => {

  const { data, setData } = useFormContext();

  const initialPostURLs = useRef(data.postURLs);

  const [urlErrors, setUrlErrors] = useState<(string | undefined)[]>([]);
  const [errorText, setErrorText] = useState<(string | undefined)>();
  const [urls, setUrls] = useState<string[]>([]);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [collapsedStates, setCollapsedStates] = useState<{ [key: number]: boolean }>({});
  const [incompleteIndexes, setIncompleteIndexes] = useState<number[]>([]);
  const [attemptedSubmission, setAttemptedSubmission] = useState(false);
  const [incompleteIncidents, setIncompleteIncidents] = useState<
    Array<{ index: number; missingFields: string[] }>
  >([]);
  const [mediaUrlErrors, setMediaUrlErrors] = useState<{ [key: number]: string | undefined }>({});

  const toggleCollapse = (index: number): void => {
    setCollapsedStates({
      ...collapsedStates,
      [index]: !collapsedStates[index],
    });
  };

  const collapseAll = () => {
    const collapsedStatesUpdated = Object.fromEntries(
      data.eventMedia.map((_, index) => [index, true])
    );
    setCollapsedStates(collapsedStatesUpdated);
  }

  const ExpandAll = () => {
    const collapsedStatesUpdated = Object.fromEntries(
      data.eventMedia.map((_, index) => [index, false])
    );
    setCollapsedStates(collapsedStatesUpdated);
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    setUrls(initialPostURLs.current);
  }, [])

  useEffect(() => {
    const newList = urls.filter(urls => urls !== '');
    setData({
      ...data,
      postURLs: newList,
    });
  }, [urls, setData]);

  useEffect(() => {
    const hasAtLeast3Posts = data.postURLs.length >= 3;
    const newCompleteIndexes: number[] = [];
    const newIncompleteIndexes: number[] = [];
    const newIncompleteIncidents: Array<{
      index: number;
      missingFields: string[];
    }> = [];
    data.eventMedia?.forEach((incident, index) => {
      // Gather all fields that are missing
      const missingFields: string[] = [];
      // Only require eventFootage for personal media sources
      if (incident.mediaSource === FootageType.Personal && incident.eventFootage.length === 0) {
        missingFields.push('eventFootage');
      }
      if (!incident.eventDate.trim()) {
        missingFields.push('eventDate');
      }
      if (!incident.location.trim()) {
        missingFields.push('location');
      }
      // Check mediaUrl only for online media sources
      if (incident.mediaSource === FootageType.Online && !incident.mediaUrl?.trim()) {
        missingFields.push('mediaUrl');
      }

      // If nothing is missing, it is complete
      if (missingFields.length === 0) {
        newCompleteIndexes.push(index);
      } else {
        newIncompleteIndexes.push(index);
        newIncompleteIncidents.push({
          index,
          missingFields,
        });
      }
    });
    setIncompleteIndexes(newIncompleteIndexes);
    setIncompleteIncidents(newIncompleteIncidents);

    // New validation logic:
    // 1. If you have 3+ posts AND no incidents, you can submit
    // 2. If you have incidents, ALL incidents must be complete (regardless of posts)
    // 3. You can't have a mix of complete and incomplete incidents
    const hasAnyIncidents = data.eventMedia.length > 0;
    const allIncidentsComplete = hasAnyIncidents && newIncompleteIndexes.length === 0;
    
    // Can submit if: (3+ posts AND no incidents) OR (all incidents are complete)
    setCanSubmit((hasAtLeast3Posts && !hasAnyIncidents) || allIncidentsComplete);
  }, [data, urls]);

  const onClickButton = (direction: Direction) => {
    if (direction === Direction.prev) {
      setCurrentStep(currentStep - 1);
    } else if (direction === Direction.next) {
      if (canSubmit) {
        setErrorText(undefined);
        setMediaUrlErrors({});
        
        // Validate social media post URLs
        const hasPost = data.postURLs.length >= 3;
        if (hasPost) {
          const newErrors = data.postURLs.map(url => {
            if (!isValidUrl(url)) return `Please enter a valid URL`;
            return undefined;
          });
          setUrlErrors(newErrors);
          if (newErrors.some(error => error !== undefined)) {
            return;
          }
        }
        
        // Validate mediaUrl fields for online incidents
        const newMediaUrlErrors: { [key: number]: string | undefined } = {};
        let hasMediaUrlError = false;
        
        data.eventMedia.forEach((incident, index) => {
          if (incident.mediaSource === FootageType.Online && incident.mediaUrl?.trim()) {
            if (!isValidUrl(incident.mediaUrl)) {
              newMediaUrlErrors[index] = 'Please enter a valid URL';
              hasMediaUrlError = true;
            }
          }
        });
        
        setMediaUrlErrors(newMediaUrlErrors);
        if (hasMediaUrlError) {
          return;
        }
        
        const filteredIncidents = data.eventMedia.filter((incident) => {
          const noFiles = incident.eventFootage.length === 0;
          const noDate = !incident.eventDate?.trim();
          const noLocation = !incident.location?.trim();
          const noDescription = !incident.description?.trim();
          // For online incidents, files are optional, so we don't consider noFiles as making it empty
          const isCompletelyEmpty = incident.mediaSource === FootageType.Online 
            ? noDate && noLocation && noDescription
            : noFiles && noDate && noLocation && noDescription;
          return !isCompletelyEmpty;
        });
        const updatedData = { ...data, eventMedia: filteredIncidents };
        setData(updatedData);
        setCurrentStep(currentStep + 1);
      }
      else {
        setAttemptedSubmission(true);
        ExpandAll();
        setErrorText('Please make sure you have included three social media posts (with no incidents), or filled out all incident reports completely');
      }
    }
    if (canSubmit) {
      markStepAsCompleted(SubmissionSteps.MediaEvidence)
    }
  }

  const handlePostInputChange = (index: number, value: string) => {
    const updatedURLs = [...urls];
    updatedURLs[index] = value;
    setUrls(updatedURLs);
  };

  const handleAddMorePosts = () => {
    collapseAll();
    const minUrls = 3;
    if (urls.length < minUrls && data.eventMedia.length === 0) {
      const newUrls = Array(minUrls - urls.length).fill('');
      setUrls([...urls, ...newUrls,]);
    } else {
      setUrls([...urls, '']);
    }
  };

  const handleRemovePost = (index: number) => {
    const updatedURLs = urls.filter((_, i) => i !== index); // Remove the URL at the given index
    setUrls(updatedURLs); // Update the state
  };

  const handleAddIncident = () => {
    collapseAll();
    setData({
      ...data, eventMedia: [...data.eventMedia, {
        eventFootage: [],
        mediaSource: FootageType.Personal,
        eventDate: '',
        location: '',
        mediaUrl: '',
        description: '',
      }]
    });
  };

  const renderSocialMediaPosts = () => {
    if (urls.length === 0) return;
    return (
      <div>
        <div className='text-[20px] font-bold leading-[35px]'>Social Media Posts</div>
        {urls.map((url, index) => (
          <div className='m-2 border-[1px] rounded-[8px] bg-gray-100 border-gray-600 p-2 md:p-4' key={index}>
            <div className="flex items-center flex-col md:flex-row">
              <TitleAndInput
                title={'Post URL'}
                required={false}
                hideOptional={true}
                errorText={urlErrors[index]}
                type={'url'}
                inputValue={url}
                name={`url`}
                placeholder={"Enter social media URL"}
                handleFieldChange={(e) => handlePostInputChange(index, e.target.value)}
              />
              <button
                className="md:mt-6 ml-2 bg-cm-red !text-white rounded-lg w-[100px] h-[40px] my-2"
                onClick={() => handleRemovePost(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderOnlineFields = (incident: EventMediaType, index: number, isFieldMissing: any) => {
    const mediaUrlError = mediaUrlErrors[index] || (isFieldMissing('mediaUrl') ? 'Please fill in required field' : '');
    
    return (
      <>
        <TitleAndInput additionalClass={'p-15 w-fit'} errorText={isFieldMissing('eventDate') ? 'Please fill in required field' : ''} title='Date this media was published online' required={true} type={'date'} inputValue={incident.eventDate} name={`eventDate-${index}`} placeholder={''} handleFieldChange={(e) => handleIncidentChange(e, index)} /> 
        <TitleAndInput title='Location' required={true} errorText={isFieldMissing('location') ? 'Please fill in required field' : ''} type={'string'} inputValue={incident.location} name={`location-${index}`} placeholder={'Enter location'} handleFieldChange={(e) => handleIncidentChange(e, index)} />
        <TitleAndInput title='Original Media URL' errorText={mediaUrlError} required={true} type={'url'} inputValue={incident.mediaUrl} name={`mediaUrl-${index}`} placeholder={'Enter original media URL'} handleFieldChange={(e) => handleIncidentChange(e, index)} />
      </>
    )
  };

  const handleRemoveIncident = (index: number) => {
    const filteredInfo = data.eventMedia.filter((_, i) => i !== index);
    setData({ ...data, eventMedia: filteredInfo });
  };

  const removeFile = (e: React.MouseEvent<HTMLButtonElement>, toRemove: File) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedEventMedia = data.eventMedia.map((incident) => ({
      ...incident,
      eventFootage: incident.eventFootage.filter((file) => file !== toRemove),
    }));
    setData({
      ...data,
      eventMedia: updatedEventMedia,
    });
  };

  const handleIncidentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, index: number) => {
    const { name, value } = e.target;
    const updatedEventMedia = [...data.eventMedia];
    updatedEventMedia[index] = {
      ...updatedEventMedia[index],
      [name.split('-')[0]]: value,
    };
    setData({
      ...data,
      eventMedia: [...updatedEventMedia],
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderPersonalMediaFields = (incident: EventMediaType, index: number, isFieldMissing: any) => {
    return (
      <>
        <TitleAndInput additionalClass={'p-15 w-fit'} errorText={isFieldMissing('eventDate') ? 'Please fill in required field' : ''} title='Date this media was filmed' required={true} type={'date'} inputValue={incident.eventDate} name={`eventDate-${index}`} placeholder={''} handleFieldChange={(e) => handleIncidentChange(e, index)} />
        <TitleAndInput title='Location' required={true} errorText={isFieldMissing('location') ? 'Please fill in required field' : ''} type={'string'} inputValue={incident.location} name={`location-${index}`} placeholder={'Enter location'} handleFieldChange={(e) => handleIncidentChange(e, index)} />
      </>
    )
  };

  const ChevronSvg = (isCollapsed: boolean) => {
    return (
      <SvgIcon
        width="20"
        height="20"
        fill="currentColor"
        className={`bi bi-chevron-up text-sm/6 font-bold relative xl:left-[4px] left:left-0 ${isCollapsed ? 'transform rotate-180' : ''}`}
        viewBox="0 0 16 16"
      >
        <path
          fillRule="evenodd"
          d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"
        />
      </SvgIcon>
    )
  }

  const renderIncident = (index: number, incident: EventMediaType) => {
    const isCollapsed = collapsedStates[index] || false;
    const foundIncomplete = incompleteIncidents.find(item => item.index === index);
    const isFieldMissing = (fieldName: string) => {
      return attemptedSubmission && foundIncomplete?.missingFields.includes(fieldName);
    };
    return (
      <div key={index} className={`border-[1px] m-2 rounded-[8px] bg-gray-100 ${attemptedSubmission && incompleteIndexes.includes(index) ? 'border-red-500 border-2' : 'border-gray-600'} p-4`}>
        <button
          className="flex focus:outline-none w-full justify-between items-center"
          onClick={() => toggleCollapse(index)}
          aria-label="Toggle Collapse"
        >
          <div className="text-[20px] font-semibold leading-[35px]">Incident #{index + 1}</div>
          <div>{ChevronSvg(isCollapsed)}</div>
        </button>
        {isCollapsed ? null : <>
          <div className='text-[16px] font-medium leading-[24px]'>
            Incident Footage {incident.mediaSource === FootageType.Online ? (
              <span className={'text-[12px] ml-1'}>(Optional)</span>
            ) : (
              <span className={`text-red-700`}>*</span>
            )}
          </div>
          <DropZone target="eventMedia" index={index} hasError={incident.mediaSource === FootageType.Personal && isFieldMissing('eventFootage')} />
          <div className={'flex flex-col mt-2'}>
            {incident.eventFootage?.map((photo, i) => {
              return (
                <div key={i} className={'flex flex-row mt-1 items-center p-2 justify-between w-full'}>
                  <div className="text-[14px] flex items-center break-all" title={photo.name}>
                    <div>{photo.type.startsWith('video') ? videoIcon : fileIcon}</div>
                    <div className={'flex flex-col ml-2'}>
                      <div>{photo.name}</div>
                      <div>{getPhotoSizeString(photo)}</div>
                    </div>
                  </div>
                  <button
                    className={`w-[24px] h-[24px] flex items-center justify-center !text-white ml-[20px] text-white`}
                    onClick={(e) => removeFile(e, incident.eventFootage[i])}>
                    {trash}
                  </button>
                </div>
              )
            })}
          </div>
          <div className="w-full m-2 text-center text-[16px] font-medium leading-[24px]">This media was:</div>
          <div className="flex space-x-4 justify-center items-center m-3 w-full">
            <div className="flex md:items-center">
              <input type="radio" name={`mediaSource-${index}`} id="foundOnline" value={FootageType.Online} onChange={(e) => handleIncidentChange(e, index)} checked={incident.mediaSource === FootageType.Online} />
              <div className='ml-[2px] md:ml-1 text-[16px] font-medium leading-[16px] md:leading-[24px]'>Found Online</div>
            </div>
            <div className='flex md:items-center'>
              <input type="radio" name={`mediaSource-${index}`} id="personalFootage" value={FootageType.Personal} onChange={(e) => handleIncidentChange(e, index)} checked={incident.mediaSource === FootageType.Personal} />
              <div className='ml-[2px] md:ml-1 text-[16px] font-medium leading-[16px] md:leading-[24px]'>Personal Footage</div>
            </div>
          </div>
          {incident.mediaSource === FootageType.Online ? renderOnlineFields(incident, index, isFieldMissing) : renderPersonalMediaFields(incident, index, isFieldMissing)}
          <div className="flex flex-col">
            <div className='text-[16px] font-medium leading-[24px] mb-1'>
              Description
            </div>
            <textarea className={`min-h-[100px]`} value={incident.description} onChange={(e) => handleIncidentChange(e, index)} id="description" placeholder="Enter event description" name={`description-${index}`} />
          </div>
          <button className="bg-gray-600 mr-2 !text-white rounded-lg w-[100px] h-[30px] my-[20px] h-[40px]" onClick={() => toggleCollapse(index)}>
            Done
          </button>
          <button className="bg-cm-red !text-white rounded-lg w-[100px] h-[30px] my-[20px] h-[40px]" onClick={() => handleRemoveIncident(index)}>
            Remove
          </button>
        </>}
      </div>
    )
  }

  const renderAllIncidents = () => {
    if (data.eventMedia.length === 0) return;
    return (
      <div>
        {data.eventMedia.map((incident, index) => {
          return renderIncident(index, incident)
        })
        }
      </div>
    );
  }

  return (
    <div>
      <div className="text-[20px] font-bold mb-[10px] leading-[30px] md:text-[30px] md:leading-[45px]">
        Evidence
      </div>
      <div className="text-[14px] font-medium leading-[21px] mb-[20px] md:text-[16px] md:leading-[24px]">
        <div>
          Clear, unambiguous evidence of antisemitic activity is a requirement for publication on our website. In order to help our research
          team investigate this individual, either one of the following pieces of evidence must be submitted:
        </div>
        <br />
        <div className={'ml-1'}>
          <div>
            1. Incident Media
          </div>
          <div className="flex items-start ml-2">
            <div>•</div>
            <div className={'ml-2'}>
              <strong>One or more</strong> video or photographic evidence of this individual engaged in antisemitic activity (e.g., Protests, Encampments, Class Disruptions, Violence, etc.). A description of the context behind the incident must be included.
            </div>
          </div>
        </div>
        <div className='text-center mt-2'>or</div>
        <div className={'ml-1 mt-2'}>
          <div>
            2. Social Media Posts
          </div>
          <div className="flex items-start ml-2">
            <div>•</div>
            <div className="ml-2">
              <strong>At least Three</strong> posts from Social Media platforms in which the individual is espousing antisemitic rhetoric.
            </div>
          </div>
        </div>
      </div>
      {renderSocialMediaPosts()}
      {renderAllIncidents()}
      {errorText ? <div className={'text-red-500 italic text-sm mt-2'}>{errorText}</div> : null}
      <div className='flex flex-col md:flex-row justify-center w-full items-center'>
        <button className={'rounded-lg flex justify-center items-center w-[290px] h-[40px] bg-gray-600 !text-white text-base font-medium leading-6 m-3'} onClick={handleAddIncident}>
          <div className='mr-1'>Add an Incident</div>
          {addIcon}
        </button>
        <button
          className={'rounded-lg flex justify-center items-center w-[290px] h-[40px] bg-gray-400 text-base font-medium leading-6 m-3'}
          onClick={handleAddMorePosts}
        >
          <div className='mr-1'>Add Social Media Posts</div>
          {addIcon}
        </button>
      </div>
      <div className={'flex flex-row justify-center align-center my-2 p-3 w-full border-t border-gray-300'}>
        <StepNavigation
          onClickNext={() => onClickButton(Direction.next)}
          hasNext={true}
          hasPrev={true}
          onClickPrev={() => onClickButton(Direction.prev)}
          isNextDisabled={!canSubmit}
        />
      </div>
    </div>
  );
}